export enum ArtifactType {
  Apk = 'apk',
  Config = 'config',
  Media = 'media',
  Os = 'os',
  Ota = 'ota',
}

export interface AssetArtifacts {
  apks: ApkArtifact[];
  bootanimations: MediaArtifact[];
  splashScreens: MediaArtifact[];
}

export interface Artifact {
  name: string;
  type: ArtifactType;
  version: string;
  createdAt: string;
}

export interface MediaArtifactSlim extends Artifact {
  media: {
    type: 'bootanimation' | 'splash';
  };
}

export interface ConfigAppMetadataModel {
  name: string;
  package_name: string;
  version_code: number;
}

export interface ConfigMediaMetadataModel {
  name: string;
  version: number;
}

export interface ConfigMediaModel {
  bootanimation?: ConfigMediaMetadataModel;
  splash?: ConfigMediaMetadataModel;
}

export interface ConfigModel {
  os: ConfigOSModel;
  apps?: ConfigAppMetadataModel[];
  media?: ConfigMediaModel;
  apns?: Record<string, unknown>;
  wifi?: Record<string, unknown>;
  properties?: Record<string, unknown>;
}

export interface ConfigOSModel {
  name: string;
  // string type latest is purely for display in the front end. Api only accepts integer.
  // Use os.version = 0 to tell backend to autoincrement.
  version: number | 'latest';
  configurations?: Record<string, unknown>;
}

export interface ArtifactBase {
  uuid?: string;
  checksum: {
    sha1: string;
  };
  composite: string;
  createdAt: string;
  customer: string;
  name: string;
  url: string;
  version: string;
  releaseVersion?: string;
}

export interface ApkMetadata {
  packageName: string;
  versionCode: string;
  versionName?: string;
}

export interface ApkArtifact extends ArtifactBase {
  type: ArtifactType.Apk;
  apk: ApkMetadata;
}
export interface ConfigArtifact extends ArtifactBase {
  type: ArtifactType.Config;
  config?: ConfigModel;
  configYaml?: string; //  stringified version of ConfigModel, returned from gql
}
export interface MediaArtifact extends ArtifactBase {
  type: ArtifactType.Media;
  media: MediaArtifactMetadata;
}

export interface MediaArtifactMetadata {
  type: string;
}
export interface OsArtifact extends ArtifactBase {
  type: ArtifactType.Os;
  os?: {
    buildUrl: string;
    flavor: string;
    id: string;
    product: string;
    release: string;
    tags: string;
    type: string;
    user: string;
  };
}
export interface OtaArtifact extends ArtifactBase {
  type: ArtifactType.Ota;
  ota: {
    destination: {
      name: string;
      url: string;
      version: string;
    };
    source: {
      name: string;
      url: string;
      version: string;
    };
  };
}

// discriminated union type based on this schema:
// https://github.com/MasonAmerica/registry-service/blob/473a0373a6daefaf4fab08e1b978fca16e508909/server/lib/util.js#L226
export type ArtifactModel = ApkArtifact | ConfigArtifact | MediaArtifact | OsArtifact | OtaArtifact;

export type GetArtifactsResult = ArtifactModel[];

export interface GetArtifactsOptions {
  customerId: string;
  type?: ArtifactType;
  name?: string;
  version?: string | number;
}

export interface GetConfigArtifactOptions {
  customerId: string;
  name: string;
  version: string | number;
}

export interface GetArtifactsOption {
  customerId: string;
  type: ArtifactType;
  name: string;
  version: string | number;
}

export type GetArtifactsOptionsList = GetArtifactsOption[];

export interface CreateArtifactDeploymentOptions {
  customer: string;
  group: string;
  name: string;
  version: string;
  type: ArtifactType;
  push?: boolean;
  deployInsecure?: boolean;
}

export interface ArtifactDeployment {
  artifactName: string;
  artifactType: ArtifactType;
  artifactVersion: string;
  createdAt: string;
  customer: string;
  deployInsecure: boolean;
  groupId: string;
  uuid: string;
}

export interface CreateArtifactDeploymentResult {
  data: ArtifactDeployment;
  message: string;
}

export type ArtifactCreationType = 'apk' | 'splash' | 'bootanimation' | 'config';

export interface CreateArtifactOptions {
  type: ArtifactCreationType;
  file: Blob;
  fileName: string;
}

export interface ValidateConfigResult extends GetArtifactsOption {
  missing?: boolean;
}
